import { Box, Card, CardContent, Grid, Typography, Skeleton } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useQuery } from '@tanstack/react-query';
import { accountsApi, Account } from '../api/accounts';

function StatCard({ title, value, icon, color }: {
  title: string; value: string; icon: React.ReactNode; color: string;
}) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ bgcolor: color, borderRadius: 2, p: 1.5, display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h5" fontWeight={600}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { data: accountsRes, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  const accounts: Account[] = accountsRes?.data ?? [];
  const totalBalance = accounts.reduce((sum, a) => sum + parseFloat(a.balance || '0'), 0);
  const activeCount = accounts.filter((a) => a.status === 'ACTIVE').length;

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Dashboard</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}><Skeleton variant="rounded" height={100} /></Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Dashboard</Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard title="Total Balance" value={fmt(totalBalance)}
            icon={<AccountBalanceIcon sx={{ color: 'white' }} />} color="primary.main" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Active Accounts" value={String(activeCount)}
            icon={<TrendingUpIcon sx={{ color: 'white' }} />} color="success.main" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Total Accounts" value={String(accounts.length)}
            icon={<ReceiptLongIcon sx={{ color: 'white' }} />} color="info.main" />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>Accounts</Typography>
      <Grid container spacing={2}>
        {accounts.map((account) => (
          <Grid item xs={12} sm={6} md={4} key={account.id}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {account.accountType} &middot; {account.accountNumber}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {fmt(parseFloat(account.balance))}
                </Typography>
                <Typography variant="caption" color={account.status === 'ACTIVE' ? 'success.main' : 'error.main'}>
                  {account.status}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {accounts.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary">No accounts found.</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
